/**
 * AuthProvider — session + RBAC context.
 *
 * The backend is the source of truth for security; this context only powers a
 * coherent UX (route guards, showing/hiding actions). Permissions for the
 * current role are fetched from the backend RBAC matrix (/meta/rbac).
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import * as authService from '@/services/auth/authService';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
        const [user, setUser] = useState(undefined); // undefined = checking, null = anon
        const [permissions, setPermissions] = useState([]);

        const loadPermissions = useCallback(async (role) => {
                try {
                        const roles = await authService.fetchRbacMatrix();
                        const found = roles.find((r) => r.key === role);
                        setPermissions(found ? found.permissions : []);
                } catch {
                        setPermissions([]);
                }
        }, []);

        const bootstrap = useCallback(async () => {
                try {
                        const u = await authService.me();
                        setUser(u);
                        await loadPermissions(u.role);
                } catch {
                        setUser(null);
                        setPermissions([]);
                }
        }, [loadPermissions]);

        useEffect(() => {
                bootstrap();
        }, [bootstrap]);

        const login = useCallback(
                async (credentials) => {
                        const { user: u } = await authService.login(credentials);
                        setUser(u);
                        await loadPermissions(u.role);
                        return u;
                },
                [loadPermissions]
        );

        const logout = useCallback(async () => {
                try {
                        await authService.logout();
                } finally {
                        setUser(null);
                        setPermissions([]);
                }
        }, []);

        /** UX-only permission check (server still enforces). */
        const can = useCallback((permission) => permissions.includes(permission), [permissions]);
        const hasRole = useCallback((...roles) => !!user && roles.includes(user.role), [user]);

        const value = {
                user,
                permissions,
                loading: user === undefined,
                isAuthenticated: !!user,
                login,
                logout,
                refreshSession: bootstrap,
                can,
                hasRole,
        };

        return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
        const ctx = useContext(AuthContext);
        if (!ctx) throw new Error('useAuth must be used within AuthProvider');
        return ctx;
}
